import {ConfirmationDialog, FormDialog, ReorderingPanel} from '@management-ui/core';
import {Delete, Edit} from '@mui/icons-material';
import numeral from 'numeral';
import React, {useCallback, useContext, useEffect, useState} from 'react';
import {ServiceContext} from '../../../../components/Services';
import {formatCurrency} from '../../../../utilities';
import OrderItemForm from '../../forms/OrderItemForm';

const Items = ({list, onListUpdated}) => {
  const services = useContext(ServiceContext);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selected, setSelected] = useState({});

  const formatItems = useCallback((list) => (
    list.map((item) => ({
      entity: item,
      attributes: [
        [
          item.material?.name ?? '',
          item.format?.name ?? '',
          item.colour?.name ?? ''
        ].filter(d => !!d).join(' - '),
        item.commodity_code ?? '-',
        item.price ? formatCurrency(item.price, list.currency) : '-',
        [
          ...(item.weight ? [
            `${numeral(item.weight).format('0,0.00')} MT`,
            ...(item.final_weight ? [` (Final: ${numeral(item.final_weight).format('0,0.00')} MT)`] : [])
          ] : ['-'])
        ].join(' ')
      ]
    }))
  ), []);

  useEffect(() => {
    setItems(formatItems(list ? list.items : []));
  }, [list, formatItems]);

  const handleReorder = useCallback((reordered) => {
    setItems(formatItems(reordered));
    return services.packingList.item.reorderItems(list, reordered.map((item, index) => ({
      id: item.id,
      index: index + 1
    })));
  }, [list, services, formatItems]);

  const handleDelete = useCallback((confirmed) => {
    setShowDelete(false);
    if (confirmed) {
      setLoading(true);
      services.packingList.item.deleteItem(list, selected).then((updated) => {
        setLoading(false);
        onListUpdated(updated);
      }).catch(() => setLoading(false));
    }
  }, [list, onListUpdated, services, selected]);

  const handleSaved = useCallback((updated) => {
    onListUpdated(updated);
    setShowEdit(false);
  }, [onListUpdated]);

  return (
    <>
      <ReorderingPanel
        title="Items"
        items={items}
        loading={loading}
        onLoading={setLoading}
        controls={list.archived || list.locked ? [] : [
          {
            title: 'Edit Item',
            icon: <Edit/>,
            onClick: (item) => setSelected(item) || setShowEdit(true)
          },
          {
            title: 'Delete Item',
            icon: <Delete/>,
            onClick: (item) => setSelected(item) || setShowDelete(true)
          }
        ]}
        onNew={list.archived || list.locked ? null : () => setSelected(null) || setShowEdit(true)}
        onReorder={list.archived || list.locked ? () => new Promise(resolve => resolve(list)) : handleReorder}
        onReordered={handleSaved}
      />
      <FormDialog
        title={selected ? 'Edit Item' : 'Add New Item'}
        open={showEdit}
        maxWidth="md"
        onClose={() => setShowEdit(false)}
        render={(props) => (
          <OrderItemForm
            {...props}
            service={services.packingList}
            order={list}
            item={selected ?? {}}
            showCommodity
            onSaved={handleSaved}
          />
        )}
      />
      <ConfirmationDialog
        title="Delete Item"
        message="Are you sure you want to delete this item?"
        open={showDelete}
        onClose={handleDelete}
      />
    </>
  );
}

export default Items;
